import * as React from "react";
import type { ColumnDef } from "@tanstack/react-table";

import {
  Alert,
  Button,
  DataTable,
  DescriptionList,
  FormField,
  notify,
  Spinner,
} from "Components/ui";
import {
  Ban,
  CalendarClock,
  Copy,
  Flag,
  IndianRupee,
  Mail,
  Pencil,
  Send,
} from "Components/ui/icons";
import { Invoice, InvoiceLineItem, PaymentLink } from "api/billing";
import { getApiErrorMessage } from "api/apiError";
import { cn } from "utils/cn";
import { copyToClipboard } from "utils/clipboard";
import {
  addDaysToDate,
  describeOffsetDays,
  formatDate,
  formatDateTime,
  formatINR,
  humanize,
  isDateOnly,
  todayIST,
} from "utils/format";
import ConfirmDialog from "Routes/Billing/Components/ConfirmDialog";
import DialogShell from "Routes/Billing/Components/DialogShell";
import {
  InvoiceStatusChip,
  PAYMENT_METHOD_LABELS,
  PaymentStatusChip,
} from "Routes/Billing/Components/StatusChips";
import { DateInput } from "Routes/Billing/Components/ui";
import { useInvoiceActions, useInvoiceDetail } from "../data";
import { BillingPermissions } from "../types";
import ActivityFeed from "./ActivityFeed";

type InvoiceDialog = "issue" | "void" | "extend" | "remind" | "resolve" | null;

interface InvoiceDetailModalProps {
  tenantId: string;
  invoiceId: string;
  permissions: BillingPermissions;
  /** Emails / WhatsApp numbers a reminder would reach now. */
  reminderRecipients: string[];
  onClose: () => void;
  onRecordPayment: (invoiceId: string) => void;
  onEdit: (invoice: Invoice) => void;
}

const SECTION_TITLE = "text-sm font-semibold text-foreground";

const itemColumns: ColumnDef<InvoiceLineItem>[] = [
  {
    id: "description",
    header: "Description",
    cell: ({ row }) => <span className="text-foreground">{row.original.description}</span>,
  },
  {
    id: "qty",
    header: "Qty",
    meta: { align: "right" },
    cell: ({ row }) => (
      <span className="tabular-nums text-muted-foreground">{row.original.qty}</span>
    ),
  },
  {
    id: "each",
    header: "Each",
    meta: { align: "right" },
    cell: ({ row }) => (
      <span className="whitespace-nowrap tabular-nums text-muted-foreground">
        {formatINR(row.original.unitPrice)}
      </span>
    ),
  },
  {
    id: "amount",
    header: "Amount",
    meta: { align: "right" },
    cell: ({ row }) => (
      <span
        className={cn(
          "whitespace-nowrap tabular-nums",
          row.original.amount < 0 ? "text-success-text" : "text-foreground"
        )}
      >
        {formatINR(row.original.amount)}
      </span>
    ),
  },
];

const linkColumns: ColumnDef<PaymentLink>[] = [
  {
    id: "reference",
    header: "Reference",
    cell: ({ row }) => (
      <span className="break-all font-mono text-xs text-foreground">
        {row.original.referenceId || row.original.linkId}
      </span>
    ),
  },
  {
    id: "amount",
    header: "Amount",
    meta: { align: "right" },
    cell: ({ row }) => (
      <span className="whitespace-nowrap tabular-nums text-foreground">
        {formatINR(row.original.amount)}
      </span>
    ),
  },
  {
    id: "status",
    header: "Status",
    cell: ({ row }) => <span className="text-foreground">{humanize(row.original.status)}</span>,
  },
  {
    id: "created",
    header: "Created",
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {formatDateTime(row.original.createdAt)}
      </span>
    ),
  },
  {
    id: "expires",
    header: "Expires",
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {formatDateTime(row.original.expireBy)}
      </span>
    ),
  },
];

export const copyPayLink = async (payUrl: string | null) => {
  if (!payUrl) {
    notify.error("No pay link yet. Set SAP_PUBLIC_BASE_URL on the server to enable pay links.");
    return;
  }
  const copied = await copyToClipboard(payUrl);
  if (copied) {
    notify.success("Pay link copied");
  } else {
    notify.error("Couldn't copy. The link is: " + payUrl);
  }
};

const ExtendDueDateDialog: React.FC<{
  invoice: Invoice;
  onConfirm: (dueDate: string, reason: string) => Promise<unknown>;
  onClose: () => void;
}> = ({ invoice, onConfirm, onClose }) => {
  const today = todayIST();
  const minDate = addDaysToDate(invoice.dueDate, 1);
  // A week from now, or a week after the old date if that is later: an overdue invoice moved to
  // another past date would stay overdue (and read-only).
  const suggested = [addDaysToDate(invoice.dueDate, 7), addDaysToDate(today, 7)].sort()[1];
  const [dueDate, setDueDate] = React.useState<string>(suggested);
  const blockingError = !isDateOnly(dueDate)
    ? "Pick the new due date."
    : dueDate <= invoice.dueDate
    ? `The new date must be after ${formatDate(invoice.dueDate)}.`
    : null;
  const stillPast = !blockingError && dueDate < today;
  return (
    <ConfirmDialog
      title="Extend due date"
      description={
        <>
          Moves {invoice.number || "this invoice"} from{" "}
          <strong className="font-semibold text-foreground">{formatDate(invoice.dueDate)}</strong>{" "}
          to a later date. Warnings and read-only move with it.
        </>
      }
      confirmLabel="Extend due date"
      requireReason
      blockingError={blockingError}
      onConfirm={(reason) => onConfirm(dueDate, reason)}
      onClose={onClose}
    >
      <FormField
        label="New due date"
        id="extend-due-date"
        required
        hint={
          stillPast
            ? "This date has already passed, so the invoice stays overdue and any read-only stays on."
            : undefined
        }
      >
        <DateInput id="extend-due-date" value={dueDate} min={minDate} onChange={setDueDate} />
      </FormField>
    </ConfirmDialog>
  );
};

/** Invoice drawer: items, payments, links, reminders, history and the invoice actions. */
const InvoiceDetailModal: React.FC<InvoiceDetailModalProps> = ({
  tenantId,
  invoiceId,
  permissions,
  reminderRecipients,
  onClose,
  onRecordPayment,
  onEdit,
}) => {
  const detail = useInvoiceDetail(invoiceId);
  const actions = useInvoiceActions(tenantId);
  const [dialog, setDialog] = React.useState<InvoiceDialog>(null);
  const invoice = detail.data ? detail.data.invoice : null;
  const today = todayIST();

  const isDraft = invoice?.status === "draft";
  const isOpen = invoice?.status === "issued" || invoice?.status === "partially_paid";
  // Invoices with money on them are corrected by reversing the payments first (the API refuses).
  const canVoid = isDraft || invoice?.status === "issued";
  const overdue = Boolean(isOpen && invoice && invoice.dueDate < today && invoice.balance > 0);

  const renderBody = () => {
    if (detail.isLoading) {
      return (
        <div className="flex items-center justify-center gap-2 px-4 py-10 text-sm text-muted-foreground">
          <Spinner className="h-5 w-5" />
          Loading invoice…
        </div>
      );
    }
    if (detail.isError || !detail.data || !invoice) {
      return (
        <Alert tone="danger" title="This invoice didn't load">
          {getApiErrorMessage(detail.error)}
        </Alert>
      );
    }
    const { payments, activity } = detail.data;
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-2">
          <InvoiceStatusChip
            status={invoice.status}
            needsReview={invoice.needsReview}
            overdue={overdue}
          />
          <span className="text-sm text-muted-foreground">
            {invoice.source === "schedule" ? "Recurring charge" : "One-time"}
            {invoice.periodIndex !== null ? ` · period ${invoice.periodIndex + 1}` : ""}
          </span>
        </div>

        {invoice.needsReview && (
          <Alert
            tone="warning"
            title="Needs review"
            action={
              permissions.canApprove ? (
                <Button variant="secondary" size="sm" onClick={() => setDialog("resolve")}>
                  Resolve
                </Button>
              ) : undefined
            }
          >
            {invoice.reviewNote || "Check the payments recorded on this invoice."}
          </Alert>
        )}
        {invoice.status === "void" && (
          <Alert tone="neutral" title={`Voided ${formatDateTime(invoice.voidedAt)}`}>
            {invoice.voidReason || "No reason recorded."}
          </Alert>
        )}

        <div className="flex flex-wrap gap-2">
          {isDraft && permissions.canWrite && (
            <>
              <Button
                size="sm"
                leftIcon={<Send aria-hidden="true" />}
                onClick={() => setDialog("issue")}
              >
                Issue
              </Button>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Pencil aria-hidden="true" />}
                onClick={() => onEdit(invoice)}
              >
                Edit
              </Button>
            </>
          )}
          {isOpen && permissions.canApprove && (
            <>
              <Button
                size="sm"
                leftIcon={<IndianRupee aria-hidden="true" />}
                onClick={() => onRecordPayment(invoice._id)}
              >
                Record payment
              </Button>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<CalendarClock aria-hidden="true" />}
                onClick={() => setDialog("extend")}
              >
                Extend due date
              </Button>
            </>
          )}
          {isOpen && invoice.balance > 0 && permissions.canWrite && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Mail aria-hidden="true" />}
              onClick={() => setDialog("remind")}
            >
              Send reminder
            </Button>
          )}
          {!isDraft && invoice.status !== "void" && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Copy aria-hidden="true" />}
              onClick={() => copyPayLink(invoice.payUrl)}
              disabled={!invoice.payUrl}
              title={invoice.payUrl || "Pay links need SAP_PUBLIC_BASE_URL on the server"}
            >
              Copy pay link
            </Button>
          )}
          {canVoid && permissions.canApprove && (
            <Button
              variant="danger-outline"
              size="sm"
              leftIcon={<Ban aria-hidden="true" />}
              onClick={() => setDialog("void")}
            >
              Void
            </Button>
          )}
        </div>

        <div className="rounded-card border border-border bg-surface-muted/40 p-4">
          <DescriptionList
            columns={3}
            items={[
              {
                label: "Due date",
                value: (
                  <span className={cn(overdue && "font-semibold text-danger-text")}>
                    {formatDate(invoice.dueDate)}
                  </span>
                ),
                hint:
                  invoice.originalDueDate && invoice.originalDueDate !== invoice.dueDate
                    ? `Originally ${formatDate(invoice.originalDueDate)}`
                    : undefined,
              },
              {
                label: "Issued",
                value: invoice.issuedAt ? formatDateTime(invoice.issuedAt) : "Not yet",
              },
              {
                label: "Paid",
                value: invoice.paidAt ? formatDateTime(invoice.paidAt) : "—",
              },
              {
                label: "Total",
                value: <span className="tabular-nums">{formatINR(invoice.total)}</span>,
              },
              {
                label: "Received",
                value: <span className="tabular-nums">{formatINR(invoice.amountPaid)}</span>,
              },
              {
                label: "Balance",
                value: (
                  <span
                    className={cn(
                      "font-semibold tabular-nums",
                      invoice.balance < 0
                        ? "text-warning-text"
                        : invoice.balance > 0
                        ? "text-foreground"
                        : "text-success-text"
                    )}
                  >
                    {formatINR(invoice.balance)}
                  </span>
                ),
                hint: invoice.balance < 0 ? "Overpaid" : undefined,
              },
            ]}
          />
        </div>

        <section>
          <h4 className={SECTION_TITLE}>Items</h4>
          <DataTable
            columns={itemColumns}
            data={invoice.items}
            density="compact"
            enableSorting={false}
            caption="Invoice items"
            getRowId={(item, index) => `${item.description}-${index}`}
            className="mt-2"
            footer={
              <div className="flex items-center justify-between gap-3 text-sm font-semibold text-foreground">
                <span>Total</span>
                <span className="tabular-nums">{formatINR(invoice.total)}</span>
              </div>
            }
          />
          {invoice.notes && (
            <p className="mt-2 text-sm text-muted-foreground">Notes: {invoice.notes}</p>
          )}
        </section>

        <section>
          <h4 className={SECTION_TITLE}>Payments</h4>
          {payments.length === 0 ? (
            <p className="mt-2 text-sm text-muted-foreground">No payments yet.</p>
          ) : (
            <ul className="mt-2 divide-y divide-border rounded-card border border-border">
              {payments.map((payment) => (
                <li
                  key={payment._id}
                  className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm"
                >
                  <span className="text-foreground">
                    <span className="font-semibold tabular-nums">{formatINR(payment.amount)}</span>{" "}
                    <span className="text-muted-foreground">
                      · {PAYMENT_METHOD_LABELS[payment.method] || humanize(payment.method)}
                      {payment.instrument ? ` (${humanize(payment.instrument)})` : ""} ·{" "}
                      {formatDate(payment.paidOn || payment.paidAt)}
                      {payment.reference ? (
                        <>
                          {" · "}
                          <span className="break-all font-mono text-xs">{payment.reference}</span>
                        </>
                      ) : null}
                    </span>
                  </span>
                  <PaymentStatusChip status={payment.status} />
                </li>
              ))}
            </ul>
          )}
        </section>

        {invoice.dueDateHistory.length > 0 && (
          <section>
            <h4 className={SECTION_TITLE}>Due date changes</h4>
            <ul className="mt-2 space-y-1 text-sm text-foreground">
              {invoice.dueDateHistory.map((change, index) => (
                <li key={`${change.from}-${change.to}-${index}`}>
                  {formatDate(change.from)} → {formatDate(change.to)}
                  {change.reason ? (
                    <span className="text-muted-foreground"> · {change.reason}</span>
                  ) : null}
                  {change.at ? (
                    <span className="text-subtle-foreground"> · {formatDateTime(change.at)}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        )}

        {invoice.paymentLinks.length > 0 && (
          <section>
            <h4 className={SECTION_TITLE}>Online payment links</h4>
            <DataTable
              columns={linkColumns}
              data={invoice.paymentLinks}
              density="compact"
              enableSorting={false}
              caption="Online payment links"
              getRowId={(link) => link.linkId}
              className="mt-2"
            />
          </section>
        )}

        {invoice.reminders.length > 0 && (
          <section>
            <h4 className={SECTION_TITLE}>Reminders</h4>
            <ul className="mt-2 space-y-1.5 text-sm">
              {invoice.reminders.map((reminder, index) => (
                <li key={`${reminder.claimedAt}-${index}`} className="text-foreground">
                  <span className="font-medium">
                    {reminder.manual
                      ? "Manual reminder"
                      : reminder.offsetDays !== null
                      ? `Automatic, ${describeOffsetDays(reminder.offsetDays)}`
                      : "Automatic"}
                  </span>
                  <span className="text-muted-foreground">
                    {" "}
                    ·{" "}
                    {reminder.error
                      ? `failed: ${reminder.error}`
                      : reminder.skipped
                      ? "skipped (too late)"
                      : reminder.sentAt
                      ? `sent ${formatDateTime(reminder.sentAt)}`
                      : "sending…"}
                    {reminder.recipients.length > 0 ? ` · ${reminder.recipients.join(", ")}` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section>
          <h4 className={SECTION_TITLE}>History</h4>
          <div className="mt-3">
            <ActivityFeed entries={activity} emptyText="No history yet." />
          </div>
        </section>
      </div>
    );
  };

  return (
    <>
      <DialogShell
        title={
          invoice ? (
            <>
              <span className={cn(invoice.number && "font-mono")}>
                {invoice.number || "Draft invoice"}
              </span>
              {invoice.title ? ` · ${invoice.title}` : ""}
            </>
          ) : (
            "Invoice"
          )
        }
        subtitle={detail.data?.tenant?.name}
        size="lg"
        onClose={onClose}
        footer={
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        }
      >
        {renderBody()}
      </DialogShell>

      {invoice && dialog === "issue" && (
        <ConfirmDialog
          title="Issue this invoice?"
          description={`It gets an invoice number and a pay link for ${formatINR(
            invoice.total
          )}, due ${formatDate(invoice.dueDate)}. Issued invoices can only be voided, not edited.`}
          confirmLabel="Issue invoice"
          onConfirm={() => actions.issue.mutateAsync(invoice._id)}
          onClose={() => setDialog(null)}
        />
      )}
      {invoice && dialog === "remind" && (
        <ConfirmDialog
          title="Send a reminder?"
          description={
            reminderRecipients.length > 0
              ? `Sends ${invoice.number || "the invoice"} (${formatINR(
                  invoice.balance
                )} open) with the pay link to ${reminderRecipients.join(", ")}.`
              : "No contact can be reached: add billing contacts on the Rules tab, and make sure email or WhatsApp reminders are set up."
          }
          confirmLabel="Send reminder"
          blockingError={
            reminderRecipients.length === 0 ? "No contact can receive a reminder yet." : null
          }
          onConfirm={() => actions.remind.mutateAsync(invoice._id)}
          onClose={() => setDialog(null)}
        />
      )}
      {invoice && dialog === "void" && (
        <ConfirmDialog
          title="Void this invoice?"
          description={`${invoice.number || "This draft"} (${formatINR(
            invoice.total
          )}) will no longer be due and its pay link stops working. This can't be undone.`}
          confirmLabel="Void invoice"
          tone="danger"
          requireReason
          onConfirm={(reason) => actions.void.mutateAsync({ invoiceId: invoice._id, reason })}
          onClose={() => setDialog(null)}
        />
      )}
      {invoice && dialog === "extend" && (
        <ExtendDueDateDialog
          invoice={invoice}
          onConfirm={(dueDate, reason) =>
            actions.extend.mutateAsync({ invoiceId: invoice._id, dueDate, reason })
          }
          onClose={() => setDialog(null)}
        />
      )}
      {invoice && dialog === "resolve" && (
        <ConfirmDialog
          title="Clear the review flag?"
          description={
            <>
              <Flag className="mr-1 inline h-4 w-4 text-warning" aria-hidden="true" />
              {invoice.reviewNote || "This invoice was flagged for review."} Confirm you have
              checked it.
            </>
          }
          confirmLabel="Mark as reviewed"
          requireReason
          onConfirm={(reason) =>
            actions.resolveReview.mutateAsync({ invoiceId: invoice._id, reason })
          }
          onClose={() => setDialog(null)}
        />
      )}
    </>
  );
};

export { ExtendDueDateDialog };
export default InvoiceDetailModal;
